// ModernFileManager.jsx
import { useState, useEffect, useMemo } from 'react';
import { File, FileText, Image, Archive, Music, Video, Upload, Search, Download, Eye, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'react-toastify';

export function ModernFileManager({ onSelect, user }) {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadType, setUploadType] = useState(null); // 'pdf' | 'md' | 'json'
  const [chunking, setChunking] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openGroups, setOpenGroups] = useState({}); // { [baseTitle]: boolean }

  useEffect(() => {
    fetchFiles();
  }, [user]);

  const fetchFiles = async () => {
    if (!user?.ms_id) return;
    setIsLoading(true);
    try {
      const response = await fetch(`/api/files?ms_id=${user.ms_id}`);
      if (response.ok) {
        const data = await response.json();
        setFiles(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Fout bij het ophalen van bestanden');
    } finally {
      setIsLoading(false);
    }
  };

  const openUpload = (type, doChunking) => {
    if (!user?.is_admin) {
      toast.error("Alleen admins kunnen bestanden uploaden.");
      return;
    }
    setUploadType(type);
    setChunking(!!doChunking);
    setShowDropdown(false);
    document.getElementById('file-upload')?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    const isPdf = ext === 'pdf';
    const isMd = ext === 'md' || ext === 'markdown';
    const isJson = ext === 'json';

    if ((uploadType === 'pdf' && !isPdf) ||
        (uploadType === 'md' && !isMd) ||
        (uploadType === 'json' && !isJson)) {
      toast.error('Ongeldig bestandstype voor deze actie');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      let endpoint = '';
      if (uploadType === 'pdf') {
        endpoint = chunking ? '/api/upload' : '/api/upload?chunking=false';
      } else if (uploadType === 'md') {
        endpoint = chunking ? '/api/upload_markdown' : '/api/upload_markdown?chunking=false';
      } else if (uploadType === 'json') {
        endpoint = '/api/upload_json';
      }

      const response = await fetch(endpoint, { method: 'POST', body: formData });
      const result = await response.json();

      if (response.ok) {
        toast.success('Bestand succesvol geüpload!');
        fetchFiles();
      } else {
        toast.error(result?.message || 'Upload mislukt');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fout bij het uploaden van het bestand');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleFileSelect = (file) => {
    const fileWithUrl = { ...file, url: `/api/files/${encodeURIComponent(file.name)}` };
    setSelectedFile(fileWithUrl);
    onSelect?.(fileWithUrl);
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
      case 'doc':
      case 'docx': return FileText;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return Image;
      case 'zip':
      case 'rar':
      case '7z': return Archive;
      case 'mp3':
      case 'wav':
      case 'ogg': return Music;
      case 'mp4':
      case 'avi':
      case 'mov': return Video;
      default: return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString ?? '';
    }
  };

  // ---- Groeperen (basistitel = naam zonder extensie) ----
  const getBaseTitle = (name) => name.replace(/\.[^.]+$/, '');
  const getExt = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return files;
    return files.filter(f =>
      f.name.toLowerCase().includes(term) || getBaseTitle(f.name).toLowerCase().includes(term)
    );
  }, [files, searchTerm]);

  const groupedFiles = useMemo(() => {
    const map = new Map();
    for (const f of filtered) {
      const base = getBaseTitle(f.name);
      if (!map.has(base)) map.set(base, { baseTitle: base, items: [] });
      map.get(base).items.push(f);
    }
    return Array.from(map.values())
      .sort((a, b) => a.baseTitle.localeCompare(b.baseTitle, 'nl'))
      .map(group => ({
        ...group,
        items: group.items.slice().sort((a, b) => {
          const ea = getExt(a.name), eb = getExt(b.name);
          if (ea !== eb) return ea.localeCompare(eb, 'nl');
          return a.name.localeCompare(b.name, 'nl');
        })
      }));
  }, [filtered]);

  const toggleGroup = (group) => {
    setOpenGroups(prev => {
      const wasOpen = !!prev[group.baseTitle];
      const next = { ...prev, [group.baseTitle]: !wasOpen };
      // Openen -> automatisch PDF selecteren indien aanwezig
      if (!wasOpen) {
        const pdfFile = group.items.find(f => getExt(f.name) === 'pdf');
        if (pdfFile) handleFileSelect(pdfFile);
      }
      return next;
    });
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-dark-700">
      <div className="p-6 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bestanden</h2>
          {user?.is_admin && (
            <div className="relative inline-block text-left">
              <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className={clsx(
                  'p-2 rounded-lg inline-flex items-center',
                  isUploading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-800'
                )}
                disabled={isUploading}
                aria-label="Upload menu"
              >
                <Upload className="h-5 w-5" />
              </button>
              {showDropdown && (
                <div className="absolute right-0 z-10 mt-2 w-64 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-md shadow-lg">
                  <button
                    onClick={() => openUpload('pdf', true)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm text-gray-700 dark:text-gray-200"
                  >
                    📄 Upload & Chunk PDF
                  </button>
                  <button
                    onClick={() => openUpload('md', true)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm text-gray-700 dark:text-gray-200"
                  >
                    📝 Upload & Chunk Markdown
                  </button>
                  <button
                    onClick={() => openUpload('pdf', false)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm text-gray-700 dark:text-gray-200"
                  >
                    📄 Upload PDF
                  </button>
                  <button
                    onClick={() => openUpload('md', false)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm text-gray-700 dark:text-gray-200"
                  >
                    📝 Upload Markdown
                  </button>
                  <button
                    onClick={() => openUpload('json', false)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 text-sm text-gray-700 dark:text-gray-200"
                  >
                    🧩 Upload pages.json
                  </button>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.md,.markdown,.json"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="hidden"
                id="file-upload"
              />
            </div>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Zoek bestanden..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">Bestanden laden…</div>
        ) : groupedFiles.length === 0 ? (
          <div className="p-6 text-center">
            <File className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Geen bestanden gevonden' : 'Geen bestanden beschikbaar'}
            </p>
            {!searchTerm && (
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                Gebruik het menu om een bestand toe te voegen
              </p>
            )}
          </div>
        ) : (
          groupedFiles.map(group => (
            <div
              key={group.baseTitle}
              className={clsx(
                'rounded-lg border-2 transition-all',
                openGroups[group.baseTitle]
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-transparent bg-gray-50 dark:bg-dark-800 hover:bg-gray-100 dark:hover:bg-dark-700'
              )}
            >
              <button
                onClick={() => toggleGroup(group)}
                className="w-full px-5 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white dark:bg-dark-700 text-gray-600 dark:text-gray-300">
                    <Layers className="h-5 w-5" />
                  </div>
                  <span className="text-base font-medium text-gray-900 dark:text-white">
                    {group.baseTitle}
                  </span>
                </div>
                {openGroups[group.baseTitle]
                  ? <ChevronDown className="h-5 w-5 text-gray-400" />
                  : <ChevronRight className="h-5 w-5 text-gray-400" />}
              </button>

              {openGroups[group.baseTitle] && (
                <div className="px-3 pb-3 space-y-2">
                  {group.items.map(file => (
                    <FileRow
                      key={file.name}
                      file={file}
                      isSelected={selectedFile?.name === file.name}
                      onSelect={() => handleFileSelect(file)}
                      getFileIcon={getFileIcon}
                      formatFileSize={formatFileSize}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {isUploading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-900 dark:text-white">Bestand uploaden...</p>
          </div>
        </div>
      )}
    </div>
  );
}

function FileRow({ file, isSelected, onSelect, getFileIcon, formatFileSize, formatDate }) {
  const Icon = getFileIcon(file.name);
  return (
    <div
      onClick={onSelect}
      className={clsx(
        "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition",
        isSelected
          ? "bg-primary-100 dark:bg-primary-800/40 text-primary-700 dark:text-primary-300"
          : "bg-white dark:bg-dark-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-600"
      )}
    >
      <div className="p-2 rounded-md bg-gray-50 dark:bg-dark-800 text-gray-600 dark:text-gray-300">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate">{file.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatFileSize(file.size)} • {formatDate(file.uploaded_at)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(`/api/files/${encodeURIComponent(file.name)}`, '_blank');
          }}
          className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-500 dark:text-gray-400"
          title="Bekijk bestand"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const link = document.createElement('a');
            link.href = `/api/files/${encodeURIComponent(file.name)}`;
            link.download = file.name;
            link.click();
          }}
          className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-500 dark:text-gray-400"
          title="Download bestand"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
